import type { FaqEntry } from '@/content/faq';

/**
 * Guias de referência (`/guias/:slug`).
 *
 * São as páginas que respondem pergunta de quem ainda está pesquisando, e é
 * delas que Google e assistentes de IA mais tiram trecho para citar. Por isso a
 * regra é dura: todo fato sobre a Meta tem fonte oficial listada em `sources`,
 * e a data de `updated` muda quando o texto for revisado. Preço da Meta muda
 * por país e por trimestre; o guia explica o modelo e manda para a tabela
 * oficial em vez de copiar valores que envelhecem.
 */
export interface Guide {
  slug: string;
  title: string;
  description: string;
  /** Resposta curta, logo abaixo do título. É o trecho que costuma ser citado. */
  summary: string;
  /** AAAA-MM-DD */
  published: string;
  updated: string;
  markdown: string;
  faq: FaqEntry[];
  sources: { label: string; url: string }[];
}

export const guides: Guide[] = [
  {
    slug: 'quanto-custa-api-oficial-whatsapp',
    title: 'Quanto custa a API oficial do WhatsApp em 2026',
    description:
      'Como a Meta cobra a API oficial do WhatsApp: por mensagem de modelo entregue, utilidade grátis na janela de 24 horas e cobrança em reais no Brasil.',
    summary:
      'Desde 1º de julho de 2025 a Meta cobra a API oficial do WhatsApp por mensagem de modelo entregue. Marketing é sempre cobrado. Utilidade e autenticação são cobradas só fora da janela de 24 horas de atendimento. Mensagem que não é modelo, como a resposta a quem escreveu para você, não tem custo. No Brasil, contas elegíveis passaram a ser cobradas em reais a partir de julho de 2026.',
    published: '2026-09-17',
    updated: '2026-09-17',
    markdown: `## O custo tem duas partes

Quem usa a API oficial paga duas coisas diferentes, para duas empresas diferentes:

1. **A Meta**, pelas mensagens de modelo que ela entrega.
2. **A plataforma** que você usa para atender (a Operax, por exemplo), pela assinatura do sistema.

Vale separar as duas na hora de comparar preço. Tem fornecedor que embute a taxa da Meta numa mensalidade maior ou cobra um valor por mensagem acima do que a Meta cobra. Na Operax a cobrança da Meta vai direto para a conta da sua empresa na Meta, e a assinatura cobre só o sistema.

## Como a Meta cobra desde julho de 2025

Até junho de 2025 a Meta cobrava por conversa de 24 horas. Desde 1º de julho de 2025 a cobrança passou a ser **por mensagem de modelo entregue**.

Mensagem de modelo (template) é aquela que precisa de aprovação prévia da Meta, usada para puxar a conversa com o cliente: uma promoção, um lembrete de consulta, um código de acesso. Cada modelo tem uma categoria, e a categoria define se paga:

| Categoria | Exemplo | Quando é cobrada |
|---|---|---|
| Marketing | Promoção, convite, reativação de cliente | Sempre |
| Utilidade | Confirmação de pedido, lembrete de consulta, atualização de entrega | Só fora da janela de 24 horas |
| Autenticação | Código de acesso de uso único | Só fora da janela de 24 horas |

Mensagem que **não é modelo** não é cobrada. Na prática é o atendimento em si: o cliente escreve e você responde.

## A janela de 24 horas

Toda vez que o cliente manda mensagem, abre uma janela de atendimento de 24 horas. Dentro dela você responde livremente, sem modelo e sem custo, e modelos de utilidade também saem de graça.

Para a maioria das empresas que atendem mais do que prospectam, isso significa que o grosso do movimento não custa nada para a Meta. O custo aparece quando a empresa começa a conversa: campanha de marketing, lembrete enviado para quem não falou com você no último dia, cobrança de quem sumiu.

## Preço em reais no Brasil

A Meta publica uma tabela de preço por país e por categoria. Desde julho de 2026, contas elegíveis no Brasil podem ser cobradas em reais, o que acaba com a variação do dólar na fatura. Os valores mudam de tempos em tempos, então a referência certa é sempre a tabela oficial, linkada nas fontes abaixo.

Para utilidade e autenticação existe desconto por volume: quanto mais mensagens no mês, menor a tarifa das faixas seguintes.

## Como estimar o seu custo

Uma conta simples resolve a primeira estimativa:

1. Conte quantas mensagens **você inicia** por mês, separadas por categoria.
2. Tire as de utilidade e autenticação que saem dentro da janela de 24 horas.
3. Multiplique o que sobrar pela tarifa do Brasil na tabela da Meta.

Respostas ao cliente não entram na conta.

## Onde a Operax entra

A assinatura da Operax começa em R$ 129,90 por mês e cobre o sistema: inbox, funil, filas e os apps incluídos. A taxa da Meta aparece separada, na conta da sua empresa na Meta, sem repasse nem acréscimo. Para simular o plano pelo tamanho da equipe, use a [calculadora](/calculadora).`,
    faq: [
      {
        question: 'Responder cliente pela API oficial do WhatsApp é cobrado?',
        answer:
          'Não. Mensagem que não é modelo, como a resposta a quem escreveu para você, não é cobrada pela Meta. Dentro da janela de 24 horas, modelos de utilidade também são gratuitos.',
      },
      {
        question: 'A Meta ainda cobra por conversa?',
        answer:
          'Não. A cobrança por conversa acabou em 30 de junho de 2025. Desde 1º de julho de 2025 a Meta cobra por mensagem de modelo entregue.',
      },
      {
        question: 'A Operax cobra taxa por mensagem?',
        answer:
          'Não. A Meta cobra as mensagens direto da conta da sua empresa. A Operax cobra só a assinatura do sistema.',
      },
    ],
    sources: [
      { label: 'Meta: Pricing on the WhatsApp Business Platform', url: 'https://developers.facebook.com/docs/whatsapp/pricing' },
      { label: 'Meta: Pricing updates', url: 'https://developers.facebook.com/docs/whatsapp/pricing/updates-to-pricing' },
    ],
  },
  {
    slug: 'whatsapp-business-app-ou-api-oficial',
    title: 'WhatsApp Business app ou API oficial: qual usar',
    description:
      'WhatsApp Business ou API oficial da Meta: para quem serve cada um, o risco das soluções não oficiais e como usar os dois juntos com a coexistência.',
    summary:
      'O aplicativo WhatsApp Business é gratuito e serve para quem atende no celular, com poucas pessoas. A API oficial da Meta (Cloud API) serve para equipe atendendo o mesmo número, automação, integração com sistema e envio em volume, e é usada por meio de um provedor. Soluções que automatizam o WhatsApp Web não são oficiais e arriscam o bloqueio do número. Com a coexistência, o mesmo número pode continuar no aplicativo e passar a funcionar também pela API.',
    published: '2026-09-17',
    updated: '2026-09-17',
    markdown: `## Os três jeitos de usar WhatsApp numa empresa

**Aplicativo WhatsApp Business.** Gratuito, instalado no celular. Tem catálogo, respostas rápidas e etiquetas. Funciona bem enquanto uma ou duas pessoas dão conta do atendimento.

**API oficial da Meta (Cloud API).** Não tem aplicativo próprio: a empresa usa por meio de uma plataforma que se conecta à Meta. É o caminho para equipe inteira atender o mesmo número, distribuir conversas em fila, automatizar respostas, integrar com agenda, estoque ou cobrança e enviar mensagens em volume.

**Automação do WhatsApp Web.** Programas que simulam alguém usando o WhatsApp Web ou o aplicativo. Não são autorizados pela Meta e o número pode ser bloqueado. Para empresa que depende do WhatsApp para vender, é um risco difícil de justificar.

## Quando o aplicativo deixa de dar conta

Os sinais costumam aparecer juntos:

- mais de uma pessoa precisa responder no mesmo número;
- cliente espera resposta porque a conversa ficou no celular de quem saiu de férias;
- ninguém sabe em que etapa da venda cada contato está;
- o lembrete de consulta, a cobrança e a confirmação de pedido são feitos à mão, um por um;
- a empresa quer ligar o WhatsApp a outro sistema.

## Não precisa escolher um só: coexistência

A Meta permite conectar um número que já está no aplicativo WhatsApp Business à API oficial sem tirar ele do celular. O número continua funcionando no aplicativo e passa a funcionar também pela plataforma, com o histórico sincronizado entre os dois.

O que a Meta exige e limita nesse modo:

- aplicativo WhatsApp Business na versão 2.24.17 ou superior;
- a conexão precisa ser feita por um Provedor de Tecnologia ou parceiro da Meta;
- é possível sincronizar as conversas dos últimos 180 dias, se a empresa autorizar;
- mensagens temporárias, visualização única, localização em tempo real e listas de transmissão deixam de funcionar;
- grupos, chamadas e as ferramentas comerciais do aplicativo (catálogo, pedidos, status) não passam para a API;
- o envio fica limitado a 20 mensagens por segundo.

## Número pessoal não conecta direto

Um número que está no WhatsApp comum (pessoal) não entra na API como está. É preciso apagar a conta do WhatsApp nesse número e esperar alguns minutos, ou usar outro número.

## Como a Operax conecta

A Operax é Provedor de Tecnologia verificado pela Meta. A conexão é feita pela própria empresa, numa janela da Meta aberta dentro do painel, em cerca de cinco minutos. O passo a passo está no guia [Como conectar o WhatsApp à API oficial](/guias/como-conectar-whatsapp-api-oficial).`,
    faq: [
      {
        question: 'Usar automação do WhatsApp Web pode bloquear o número?',
        answer:
          'Pode. Essas soluções não são autorizadas pela Meta. A forma oficial de automatizar e integrar o WhatsApp é a API oficial (Cloud API).',
      },
      {
        question: 'Posso continuar usando o WhatsApp Business no celular depois de conectar à API?',
        answer:
          'Pode, pelo recurso de coexistência da Meta. O número segue no aplicativo e passa a funcionar também pela API, com o histórico sincronizado. Alguns recursos, como listas de transmissão e grupos, não funcionam nesse modo.',
      },
      {
        question: 'A API oficial do WhatsApp é paga?',
        answer:
          'A Meta cobra por mensagem de modelo entregue; responder o cliente não é cobrado. A plataforma que você usa cobra a assinatura do sistema à parte.',
      },
    ],
    sources: [
      {
        label: 'Meta: Onboarding WhatsApp Business app users (coexistence)',
        url: 'https://developers.facebook.com/docs/whatsapp/embedded-signup/custom-flows/onboarding-business-app-users',
      },
      { label: 'Meta: Pricing on the WhatsApp Business Platform', url: 'https://developers.facebook.com/docs/whatsapp/pricing' },
    ],
  },
  {
    slug: 'como-conectar-whatsapp-api-oficial',
    title: 'Como conectar o WhatsApp à API oficial, passo a passo',
    description:
      'Passo a passo para ligar o número da empresa à API oficial do WhatsApp: o que ter em mãos, as etapas da Meta e o que fazer quando o código não chega.',
    summary:
      'Para conectar o WhatsApp à API oficial pela Operax você precisa de uma conta do Facebook, do número com o aparelho por perto e do nome de exibição da empresa. A conexão acontece numa janela da própria Meta, leva cerca de cinco minutos e termina com a mensagem de teste caindo em Conversas.',
    published: '2026-09-17',
    updated: '2026-09-17',
    markdown: `## O que ter em mãos

- **Uma conta do Facebook.** Serve só para a Meta saber quem está autorizando. Nada é publicado no seu perfil.
- **O número que vai atender, com o aparelho por perto.** A Meta manda um código de confirmação por SMS ou ligação. Número fixo recebe só por ligação.
- **O nome de exibição.** É o nome que aparece para quem recebe a mensagem, normalmente o nome da empresa.

Se a empresa já tem um Portfólio Empresarial na Meta, você escolhe ele durante a conexão. Se não tem, dá para criar na hora.

## As oito etapas

1. No painel da Operax, abra **Canais** e clique em **Conectar canal → WhatsApp**.
2. Na janela da Meta, entre com a conta do Facebook.
3. Aceite os termos apresentados pela Meta.
4. Escolha ou crie o Portfólio Empresarial.
5. Escolha ou crie a conta do WhatsApp Business.
6. Informe o número e o nome de exibição.
7. Digite o código que a Meta enviar para o número.
8. Autorize a Operax a usar essa conta.

Quando a janela fecha, a Operax assina os avisos da Meta, registra o número e ativa a conexão sozinha.

## Como confirmar que funcionou

A conexão aparecer como ativa não basta. Mande uma mensagem de outro celular para o número conectado e veja se ela aparece em **Conversas**. Se aparecer, está pronto.

Para enviar mensagens que a Meta cobra, a conta precisa de uma forma de pagamento cadastrada na Meta. Se ela ficar para depois, o card do canal mostra o aviso com o atalho para a tela de pagamento.

## Quando dá problema

**"Esse número já está registrado."** O número está preso em outro lugar: no aplicativo do celular, em outra conta da API ou num provedor anterior. Se ele estava em outra plataforma, entre no Gerenciador de Negócios da Meta, desative a verificação em duas etapas do número e remova ele da conta antiga. Espere uns cinco minutos antes de tentar de novo.

**O código não chega.** Confira o número. Depois de vários pedidos a Meta bloqueia novas tentativas por 12 horas. Espere e tente pela outra via: se pediu por SMS, peça por ligação.

**O número está no WhatsApp Business do celular.** Não precisa perder o histórico: existe o caminho de coexistência, explicado em [WhatsApp Business app ou API oficial](/guias/whatsapp-business-app-ou-api-oficial).

**O nome de exibição foi recusado.** A Meta exige relação clara com a empresa. Frase promocional ou palavra genérica é recusada.

**Não aparece a conta para escolher.** Você provavelmente não é administrador do Portfólio Empresarial. Peça acesso a quem administra, ou peça para essa pessoa fazer a conexão.

## Precisa verificar a empresa na Meta?

Para começar, não. Você conecta e já atende no limite padrão de envio, que sobe sozinho com o uso e a boa qualidade das conversas. A verificação libera limites maiores e mais números, e vale a pena quando o volume crescer.`,
    faq: [
      {
        question: 'Quanto tempo leva para conectar o WhatsApp à API oficial?',
        answer: 'Pela Operax, cerca de cinco minutos, numa janela da própria Meta aberta no painel.',
      },
      {
        question: 'Preciso de conta no Facebook para usar a API oficial do WhatsApp?',
        answer:
          'Sim, uma conta pessoal. Ela serve só para a Meta registrar quem autorizou a conexão. Nada é publicado no perfil.',
      },
      {
        question: 'Preciso verificar a empresa na Meta antes de começar?',
        answer:
          'Não. A verificação libera limites maiores de envio e mais números, e pode ser feita depois, quando o volume crescer.',
      },
    ],
    sources: [
      {
        label: 'Meta: Onboarding WhatsApp Business app users (coexistence)',
        url: 'https://developers.facebook.com/docs/whatsapp/embedded-signup/custom-flows/onboarding-business-app-users',
      },
    ],
  },
  {
    slug: 'como-escolher-crm-para-whatsapp',
    title: 'Como escolher um CRM para WhatsApp: 7 perguntas antes de assinar',
    description:
      'Checklist para escolher um CRM para WhatsApp: API oficial, custo da Meta separado, fidelidade, dados, apps que crescem com a empresa e o que perguntar.',
    summary:
      'Antes de assinar um CRM para WhatsApp, confirme sete pontos: se usa a API oficial da Meta, se a taxa da Meta vem separada da assinatura, se há fidelidade, se você leva seus dados ao sair, se agenda, estoque e cobrança entram sem trocar de sistema, quanto custa cada usuário e número a mais, e quem conecta o número.',
    published: '2026-09-17',
    updated: '2026-09-17',
    markdown: `Quase toda plataforma de atendimento no WhatsApp mostra a mesma tela na demonstração: uma caixa de entrada bonita e um funil colorido. A diferença aparece depois, na fatura e no dia em que a operação cresce. Estas são as perguntas que separam uma coisa da outra.

## 1. Usa a API oficial da Meta?

É a primeira pergunta e a que elimina mais opções. Ferramenta que automatiza o WhatsApp Web não é autorizada pela Meta e coloca o número em risco de bloqueio. Pergunte com todas as letras se a conexão é pela Cloud API e se a empresa é parceira ou Provedor de Tecnologia da Meta.

## 2. A taxa da Meta vem separada?

A Meta cobra por mensagem de modelo entregue. Há fornecedor que embute isso na mensalidade ou cobra por mensagem um valor acima da tabela da Meta. Peça para ver onde a cobrança da Meta aparece. O modelo de cobrança está explicado em [Quanto custa a API oficial do WhatsApp](/guias/quanto-custa-api-oficial-whatsapp).

## 3. Tem fidelidade ou multa?

Contrato de 12 meses com multa é comum. Se a ferramenta for boa, ela não precisa prender ninguém.

## 4. Você leva seus dados ao sair?

Contatos e histórico de conversa são da sua empresa. Confirme se dá para exportar antes de cancelar e em que formato.

## 5. O sistema cresce junto, ou você troca de sistema?

Hoje você precisa de atendimento e funil. Daqui a seis meses pode precisar de agenda, estoque, cobrança por Pix ou nota fiscal. Se cada uma dessas coisas exigir outro sistema, o WhatsApp volta a ficar isolado. Prefira plataforma em que esses módulos entram quando a operação pedir, no mesmo login.

## 6. Quanto custa crescer?

Pergunte o preço de cada usuário a mais e de cada número a mais. O plano de entrada barato às vezes vira caro no terceiro atendente.

## 7. Quem conecta o número, e em quanto tempo?

Implantação de semanas, com taxa de setup, costuma indicar processo manual. Com a API oficial e o cadastro incorporado da Meta, a própria empresa conecta o número em minutos.

## Como a Operax responde a essas perguntas

1. Conexão pela API oficial da Meta. A Operax é Provedor de Tecnologia verificado pela Meta.
2. A taxa da Meta vai direto para a conta da sua empresa na Meta, sem repasse.
3. Assinatura mensal, sem fidelidade e sem multa.
4. Você exporta o histórico ao sair.
5. Agenda, estoque, Pix, NFS-e, PDV, contratos e outros entram pelo [Portal de Apps](/apps), no mesmo login.
6. Usuário e número extra têm preço público na página de [preços](/precos).
7. A própria empresa conecta o número em cerca de cinco minutos.`,
    faq: [
      {
        question: 'Qual o principal cuidado ao escolher um CRM para WhatsApp?',
        answer:
          'Confirmar que a conexão é pela API oficial da Meta. Ferramentas que automatizam o WhatsApp Web não são autorizadas e arriscam o bloqueio do número.',
      },
      {
        question: 'CRM para WhatsApp deve cobrar taxa por mensagem?',
        answer:
          'A taxa por mensagem é da Meta. O ideal é que ela apareça separada, na conta da sua empresa na Meta, sem acréscimo do fornecedor do sistema.',
      },
    ],
    sources: [
      { label: 'Meta: Pricing on the WhatsApp Business Platform', url: 'https://developers.facebook.com/docs/whatsapp/pricing' },
    ],
  },
];

export function findGuide(slug: string): Guide | undefined {
  return guides.find((g) => g.slug === slug);
}
